/**
 * PATH: packages/ui/src/components/find/findScopeRegistry.ts
 * FILE_ID: UI-FIND-REGISTRY-001
 * PURPOSE: A tiny module-level registry of "in-page find" controllers.
 *          The single global Cmd+F authority (useLayoutState / the
 *          tenant CommandPaletteProvider) consults the registry at
 *          keydown time: if a surface (email reader, kanban board, the
 *          cards+emails popup) has registered a controller, Cmd+F opens
 *          that in-page find instead of the global palette.
 * WHY:     Cmd+F was bound in 4-5 competing places; the page-scoped
 *          AdvancedSearchPopup's bubble-phase binding was shadowed by
 *          the shell's capture-phase handler (stopPropagation), so it
 *          never opened on the dashboard (BUG-2026-06-10-CMDF-POPUP-
 *          SHADOWED). One authority + one registry replaces the handler
 *          war: the shell owns the chord, the surface owns what "find
 *          here" means.
 * FLOW:
 *   ┌──────────────────────┐   ┌───────────────────┐   ┌──────────────┐
 *   │ surface mounts        │ → │ registerFind       │ → │ stack (LIFO) │
 *   │ (useFindController)   │   │ Controller         │   │              │
 *   └──────────────────────┘   └───────────────────┘   └──────────────┘
 *        Cmd+F keydown → getActiveFindController() → controller.open(q)
 * DEPENDENCIES:
 *   - react: the ergonomic registration hook (useFindController).
 * RELATED:
 *   - dashboard-layout/useLayoutState.ts (the Cmd+F authority that reads this)
 *   - global-search/openGlobalSearch.ts (sibling cross-host event bus)
 */
import { useEffect, useRef } from 'react'

export interface FindController {
  /** Stable id for the surface, e.g. 'email-reader', 'kanban-board',
   *  'advanced-search-popup'. Used for debugging / telemetry only. */
  id: string
  /** Human label for the surface ('email', 'board', …). Optional. */
  label?: string
  /** Open this surface's in-page find. Optional seed query. */
  open: (query?: string) => void
}

// Module-level LIFO stack. The most-recently registered controller is
// the active one, so a deeper / more-specific surface (an open email
// reader) wins over a page-level one (the cards+emails popup) while it
// is mounted, and cleanly hands control back when it unmounts.
const stack: FindController[] = []

/**
 * Register a controller. Returns an unregister fn that MUST be called
 * on unmount (the `useFindController` hook does this for you).
 *
 * Input:  a FindController.
 * Output: a teardown fn. Idempotent — calling it twice is a no-op.
 */
export function registerFindController(controller: FindController): () => void {
  stack.push(controller)
  let removed = false
  return () => {
    if (removed) return
    removed = true
    const i = stack.lastIndexOf(controller)
    if (i >= 0) stack.splice(i, 1)
  }
}

/**
 * The active (top-of-stack) controller, or null when none is
 * registered. Read imperatively by the Cmd+F keydown handler — no
 * React subscription, so it always reflects the live stack at the
 * moment the chord fires.
 */
export function getActiveFindController(): FindController | null {
  return stack.length > 0 ? stack[stack.length - 1] : null
}

/** Test-only: clear the stack between cases. */
export function __resetFindControllersForTest(): void {
  stack.length = 0
}

/**
 * Ergonomic registration hook. Registers `open` (kept fresh via a ref
 * so a new closure each render doesn't thrash the stack) while
 * `enabled`. Pass `enabled: false` to keep a surface mounted but NOT
 * the active find target (e.g. a hidden / background tab).
 */
export function useFindController(opts: {
  id: string
  label?: string
  enabled?: boolean
  open: (query?: string) => void
}): void {
  const { id, label, enabled = true, open } = opts
  const openRef = useRef(open)
  openRef.current = open
  useEffect(() => {
    if (!enabled) return
    const controller: FindController = {
      id,
      label,
      open: (q) => openRef.current(q),
    }
    return registerFindController(controller)
  }, [id, label, enabled])
}
